import React, { useState, useMemo } from 'react';


const genderOptions = [
  { key: 'Male', label: 'Male', image: '👨' },
  { key: 'Female', label: 'Female', image: '👩' },
  { key: 'Other', label: 'Other', image: '🧑' }
];

const primaryGoals = [
  { key: 'lose_weight', label: 'Lose weight' },
  { key: 'reduce_stress', label: 'Reduce stress' },
  { key: 'sleep_better', label: 'Sleep better' },
  { key: 'gain_muscle', label: 'Gain muscle' },
  { key: 'improve_nutrition', label: 'Improve nutrition' },
  { key: 'weight_gain', label: 'Weight gain' },
  { key: 'child_nutrition', label: 'Child nutrition' }
];

// All questions in desired order. Use `scope` to filter.
const ALL_QUESTIONS = [
  { key: 'name', type: 'text', label: 'Full name', placeholder: 'Your full name', scope: 'all', required: true },
  { key: 'mobile', type: 'text', label: 'Mobile number', placeholder: 'e.g. +91 98765 43210', scope: 'all', required: true },
  { key: 'age', type: 'number', label: 'Age', placeholder: 'Age in years', scope: 'all', required: true },
  { key: 'gender', type: 'choice-card', label: 'What is your gender?', options: genderOptions, scope: 'all', required: true },
  { key: 'marital_status', type: 'select', label: 'Marital status', options: [
    { key: 'single', label: 'Single' }, { key: 'married', label: 'Married' }, { key: 'other', label: 'Other' }
  ], scope: 'all', required: true },

  // If female -> pregnancy question
  { key: 'is_pregnant', type: 'select', label: 'Are you currently pregnant?', options: [ { key: 'no', label: 'No' }, { key: 'yes', label: 'Yes' } ], scope: ['Female'], required: true },
  { key: 'pregnancy_month', type: 'number', label: 'How many months pregnant are you?', placeholder: 'e.g. 5', scope: ['Female'], required: true },
  { key: 'expected_delivery_date', type: 'date', label: 'Expected delivery date', scope: ['Female'], required: true },

  // If married -> children count
  { key: 'children_count', type: 'number', label: 'How many children do you have?', placeholder: '0 if none', scope: 'all', required: false },

  // Primary goal
  { key: 'primary_goal', type: 'choice-card', label: 'Primary goal', desc: 'Choose what you most want to achieve', options: primaryGoals, scope: 'all', required: true },

  // Ratings
  { key: 'health_rating', type: 'select', label: 'How would you rate your overall health & well-being (1-10)?', options: Array.from({ length: 10 }, (_, i) => ({ key: `${i + 1}`, label: `${i + 1}` })), scope: 'all', required: true },
  { key: 'stress_rating', type: 'select', label: 'On a scale of 1-10, how stressed do you feel daily?', options: Array.from({ length: 10 }, (_, i) => ({ key: `${i + 1}`, label: `${i + 1}` })), scope: 'all', required: true },

  // Sleep
  { key: 'sleep_hours', type: 'number', label: 'Average sleep per night (hours)', placeholder: 'e.g. 7', scope: 'all', required: true },
  { key: 'sleep_quality', type: 'select', label: 'How would you rate your sleep quality?', options: [ { key: 'poor', label: 'Poor' }, { key: 'fair', label: 'Fair' }, { key: 'good', label: 'Good' }, { key: 'excellent', label: 'Excellent' } ], scope: 'all', required: true },

  // A few additional common wellness questions to reach a robust flow
  { key: 'meals_per_day', type: 'select', label: 'How many meals do you eat per day?', options: [ { key: '1-2', label: '1-2' }, { key: '3', label: '3' }, { key: '4-5', label: '4-5' } ], scope: 'all', required: true },
  { key: 'diet_type', type: 'select', label: 'Which diet style do you follow?', options: [ { key: 'omnivore', label: 'Omnivore' }, { key: 'vegetarian', label: 'Vegetarian' }, { key: 'vegan', label: 'Vegan' } ], scope: 'all', required: true },
  { key: 'water_intake', type: 'select', label: 'Daily water intake', options: [ { key: '<1L', label: '< 1L' }, { key: '1-2L', label: '1-2L' }, { key: '2-3L', label: '2-3L' }, { key: '>3L', label: '>3L' } ], scope: 'all', required: true },
  { key: 'exercise_freq', type: 'select', label: 'How often do you exercise?', options: [ { key: 'none', label: 'Never' }, { key: '1-2', label: '1-2 times/week' }, { key: '3-4', label: '3-4 times/week' }, { key: '5+', label: '5 or more' } ], scope: 'all', required: true },
  { key: 'injuries', type: 'text', label: 'Any past injuries or health conditions we should know about?', placeholder: 'Optional', scope: 'all', required: false },

  // Final wrap up
  { key: 'goal_timeline', type: 'select', label: 'When would you like to achieve your goal?', options: [ { key: '3m', label: '3 months' }, { key: '6m', label: '6 months' }, { key: '12m', label: '12+ months' } ], scope: 'all', required: true },
  { key: 'comments', type: 'text', label: 'Anything else to share? (optional)', placeholder: 'Any important notes', scope: 'all', required: false }
];

export default function WelcomePage() {
  const [answers, setAnswers] = useState({});
  const [index, setIndex] = useState(0);

  const activeQuestions = useMemo(() => {
    const gender = answers.gender;
    // Always include questions with scope 'all'. If gender selected, include gender-specific scope.
    return ALL_QUESTIONS.filter(q => {
      if (q.scope === 'all') return true;
      if (Array.isArray(q.scope)) return q.scope.includes(gender);
      return false;
    });
  }, [answers]);

  React.useEffect(() => {
    if (index >= activeQuestions.length) setIndex(activeQuestions.length - 1);
  }, [activeQuestions.length]);

  const q = activeQuestions[index];
  const total = activeQuestions.length;
  const percent = Math.round(((index + 1) / total) * 100);

  const update = (key, value) => setAnswers(a => ({ ...a, [key]: value }));

  const isAnswered = (question) => {
    if (!question) return false;
    const val = answers[question.key];
    if (question.required) return val !== undefined && val !== '';
    return true;
  };

  const handleNext = () => {
    if (q.required && !isAnswered(q)) return;
    // Additional logic: if gender != Female, remove pregnancy answers to avoid stale data
    if (q.key === 'gender' && answers.gender !== 'Female') {
      // remove pregnancy keys if present
      setAnswers(a => {
        const copy = { ...a };
        delete copy.is_pregnant; delete copy.pregnancy_month; delete copy.expected_delivery_date;
        return copy;
      });
    }
    if (q.key === 'marital_status' && answers.marital_status !== 'married') {
      setAnswers(a => {
        const copy = { ...a }; delete copy.children_count; return copy;
      });
    }

    if (index < total - 1) setIndex(i => i + 1);
    else handleSubmit();
  };

  const handlePrev = () => setIndex(i => Math.max(i - 1, 0));

  const handleSubmit = () => {
    // final submit: you should integrate with API here
    console.log('Submitted answers:', answers);
    alert('Form submitted — check console.');
  };

  const Card = ({ label, desc, image, onClick, selected }) => (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center w-full p-4 mb-4 border rounded-xl transition bg-white shadow-sm
        ${selected ? 'border-orange-400 bg-orange-50 ring-1 ring-orange-200' : 'border-gray-200'}
      `}
    >
      <div className="w-12 h-12 flex items-center justify-center mr-4 text-2xl rounded-lg">{image}</div>
      <div className="text-left flex-1">
        <div className="font-semibold text-lg">{label}</div>
        {desc && <div className="text-xs text-gray-500 mt-1">{desc}</div>}
      </div>
    </button>
  );

  return (
    <div className="min-h-screen flex flex-col justify-between px-5 py-7 bg-white relative">
      <div className="absolute top-5 right-5 w-10 h-10 rounded-full border flex items-center justify-center bg-orange-100">
        <span className="text-xl">🧘</span>
      </div>

      <div className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold">{q?.label}</h1>
            {q?.desc && <p className="text-gray-500 text-sm mt-1">{q.desc}</p>}
          </div>
          <div className="text-sm font-medium">{percent}%</div>
        </div>

        <div className="h-2 w-full rounded-full bg-gray-200 mb-6">
          <div className="h-2 rounded-full bg-black" style={{ width: `${percent}%` }} />
        </div>

        <div className="flex-1">
          {q?.type === 'choice-card' && q.options.map(opt => (
            <Card
              key={opt.key}
              label={opt.label}
              desc={opt.desc}
              image={opt.image}
              selected={answers[q.key] === opt.key}
              onClick={() => update(q.key, opt.key)}
            />
          ))}

          {(q?.type === 'text' || q?.type === 'number' || q?.type === 'date') && (
            <input
              type={q.type === 'number' ? 'number' : q.type === 'date' ? 'date' : 'text'}
              placeholder={q.placeholder || ''}
              value={answers[q.key] || ''}
              onChange={e => update(q.key, e.target.value)}
              className="w-full p-4 mb-4 rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-orange-300 text-lg"
            />
          )}

          {q?.type === 'select' && (
            <div className="space-y-3">
              {q.options.map(opt => (
                <Card key={opt.key} label={opt.label} image={null} selected={answers[q.key] === opt.key} onClick={() => update(q.key, opt.key)} />
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between mt-auto pb-3">
        <button
          className="h-12 px-6 rounded-full bg-gray-100 text-gray-700 font-semibold disabled:opacity-50"
          disabled={index === 0}
          onClick={handlePrev}
        >
          Previous
        </button>

        <div className="flex-1 px-4">
          <div className="text-xs text-gray-500 mb-1">{index + 1} of {total} questions</div>
        </div>

        <button
          className="h-12 px-8 rounded-full bg-black text-white font-semibold disabled:opacity-50"
          onClick={handleNext}
          disabled={q && q.required && !isAnswered(q)}
        >
          {index === total - 1 ? 'Submit' : 'Continue'}
        </button>
      </div>
    </div>
  );
}
